import { http } from '@/system/http.js';
import { getLongDateFormat } from '@/system/helpers/index.js';
import isEmpty from 'lodash/isEmpty';

export default {
    state: {
        templates: [],
        templatesLoaded: false,
        templatesLoading: false,
    },

    mutations: {
        setTemplates(state, templates) {
            state.templates = templates;
        },

        setTemplatesLoaded(state, templatesLoaded = true) {
            state.templatesLoaded = templatesLoaded;
        },

        setTemplatesLoading(state, templatesLoading = true) {
            state.templatesLoading = templatesLoading;
        },
    },

    getters: {
        templates: (state) => {
            return state.templates || [];
        },

        templatesSorted: (state) => {
            return state.templates.sort((a, b) => {
                if (a.order > b.order) {
                    return 1;
                }

                if (a.order < b.order) {
                    return -1;
                }

                if (a.order == b.order) {
                    return a.title.localeCompare(b.title, locale);
                }
            });
        },

        getTemplateById: (state, getters) => (id) => {
            return getters.templates.find(t => t.id === id);
        },

        getTagReplacementsForClient: (state, getters, rootState, rootGetters) => (client) => {
            let medcomTemplateTagSettings = 'apps.medcom.medcom_template_feed_tags.';

            /*
              This array of objects contains both the old style tag ([name]) and new style tag (@name),
              this is so that each style is backwards compatible. But this array can be used to
              convert both style of tags to their values.

              Some newer tags will not have the oldStyleTag prop, this is because they
              were added after the new style tagging system was introduced.
             */

            let tags = [];

            function addTagForAllLocales(tags, tagKey, newStyleTagKey, value) {
                for (locale of ['en', 'da', 'no', 'sv']) {
                    let tag = {
                        newStyleTag: trans(newStyleTagKey, [], locale),
                        value: value,
                    };

                    if (tagKey) {
                        tag.tag = trans(tagKey, [], locale);
                    }

                    tags.push(tag);
                }
            }

            addTagForAllLocales(
                tags,
                'tags.first_name',
                medcomTemplateTagSettings + 'first_name.tag',
                client.name.split(' ')[0],
            );

            addTagForAllLocales(
                tags,
                'tags.full_name',
                medcomTemplateTagSettings + 'full_name.tag',
                client.name,
            );

            addTagForAllLocales(
                tags,
                'tags.your_name',
                medcomTemplateTagSettings + 'your_name.tag',
                rootState.auth.user.realname,
            );

            addTagForAllLocales(
                tags,
                'tags.dob',
                medcomTemplateTagSettings + 'client_dob.tag',
                client.dob &&
                moment(client.dob).isValid() ? moment(client.dob).locale(moment_locale).format('L') : '',
            );

            addTagForAllLocales(
                tags,
                'tags.cpr',
                medcomTemplateTagSettings + 'social_security_number.tag',
                client.cpr,
            );

            addTagForAllLocales(
                tags,
                null,
                medcomTemplateTagSettings + 'client_email.tag',
                client.email,
            );

            addTagForAllLocales(
                tags,
                null,
                medcomTemplateTagSettings + 'client_phone.tag',
                client.phone,
            );

            addTagForAllLocales(
                tags,
                null,
                medcomTemplateTagSettings + 'client_address.tag',
                [client.address, client.address_line_2].filter(ad => !isEmpty(ad)).join(', '),
            );

            addTagForAllLocales(
                tags,
                null,
                medcomTemplateTagSettings + 'client_country.tag',
                client.country,
            );

            addTagForAllLocales(
                tags,
                null,
                medcomTemplateTagSettings + 'current_date_short.tag',
                moment().locale(moment_locale).format('L'),
            );

            addTagForAllLocales(
                tags,
                null,
                medcomTemplateTagSettings + 'current_date_long.tag',
                moment().locale(moment_locale).format(getLongDateFormat()),
            );

            addTagForAllLocales(
                tags,
                null,
                medcomTemplateTagSettings + 'current_weekday.tag',
                moment().locale(moment_locale).format('dddd'),
            );

            addTagForAllLocales(
                tags,
                null,
                medcomTemplateTagSettings + 'user_email.tag',
                rootState.auth.user.email,
            );

            return tags;
        },
    },

    actions: {
        loadTemplates({ state, dispatch }) {
            if (state.templatesLoaded || state.templatesLoading) return;

            dispatch('forceLoadTemplates');
        },

        forceLoadTemplates({ state, commit }) {
            commit('setTemplatesLoading', true);

            http.get('/app/medcom/templates')
                .then(({ data }) => {
                    commit('setTemplates', data);
                    commit('setTemplatesLoaded', true);
                })
                .catch(({ error }) => {
                    commit('setTemplates', []);
                    commit('setTemplatesLoaded', false);
                    console.error('Could not load medcom templates', error);
                })
                .finally(() => {
                    commit('setTemplatesLoading', false);
                });
        },
    },
};
